'use client';

import { useEffect, useState } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type Theme = 'light' | 'dark' | 'system';

export default function ThemeSwitcher() {
    const [theme, setTheme] = useState<Theme>('system');
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        const stored = localStorage.getItem('theme') as Theme | null;
        if (stored) {
            setTheme(stored);
        }
    }, []);

    useEffect(() => {
        if (!mounted) return;

        const root = document.documentElement;

        if (theme === 'system') {
            localStorage.removeItem('theme');
            root.removeAttribute('data-theme');
        } else {
            localStorage.setItem('theme', theme);
            root.setAttribute('data-theme', theme);
        }
    }, [theme, mounted]);

    if (!mounted) {
        return (
            <div className="w-10 h-10 rounded-full bg-[var(--color-md-surface-variant)] animate-pulse" />
        );
    }

    const themes: { value: Theme; icon: React.ReactNode; label: string }[] = [
        { value: 'light', icon: <Sun className="w-4 h-4" />, label: 'Light' },
        { value: 'dark', icon: <Moon className="w-4 h-4" />, label: 'Dark' },
        { value: 'system', icon: <Monitor className="w-4 h-4" />, label: 'System' },
    ];

    return (
        <div className="relative flex items-center gap-0.5 p-0.5 rounded-full bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline-variant)]/50 shadow-sm">
            {themes.map((t) => (
                <button
                    key={t.value}
                    onClick={() => setTheme(t.value)}
                    className={`
                        relative p-2 rounded-full transition-colors duration-200 z-10
                        ${theme === t.value
                            ? 'text-[var(--color-md-on-surface)]'
                            : 'text-[var(--color-md-on-surface-variant)] hover:text-[var(--color-md-on-surface)]'
                        }
                    `}
                    aria-label={t.label}
                    title={t.label}
                >
                    {theme === t.value && (
                        <motion.div
                            layoutId="theme-indicator"
                            className="absolute inset-0 bg-[var(--color-md-surface)] rounded-full shadow-sm"
                            style={{ borderRadius: '9999px' }}
                            transition={{
                                type: "spring",
                                stiffness: 400,
                                damping: 35,
                                mass: 0.8
                            }}
                        />
                    )}
                    <span className="relative z-10">{t.icon}</span>
                </button>
            ))}
        </div>
    );
}
